import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Smart Meal Planning and Preparation',
    content: [
      '<strong>Goal:</strong> To master the art of planning and preparing healthy, balanced meals that fit your lifestyle and budget.'
    ],
    additionalSection: {
      title: 'The Power of Meal Planning',
      content: [
        '<strong>Core Concept:</strong> Meal planning helps you eat healthier, save money, reduce waste, and manage your time better.',
        '<strong>Economic Impact:</strong> Planning meals can reduce food expenses by 25-30% through smart shopping and less waste.',
        '<strong>Health Benefits:</strong> Planned meals tend to be more nutritionally balanced and portion-controlled.',
        '<br/>',
        '<strong>Key Message:</strong> "Plan ahead, eat smart, live well."'
      ]
    },
    additionalSection2: {
      title: 'Learning Objectives',
      content: [
        '<strong>1. Planning Skills:</strong> Create effective weekly meal plans.',
        '<strong>2. Smart Shopping:</strong> Learn to shop efficiently and economically.',
        '<strong>3. Food Prep:</strong> Master basic meal preparation techniques.',
        '<strong>4. Storage Solutions:</strong> Understand proper food storage methods.'
      ]
    }
  },
  {
    title: 'Creating Your Weekly Meal Plan',
    content: [
      'Follow this structured approach to meal planning:',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header">Step</div>
          <div class="table-header">Action</div>
          <div class="table-header">Tips</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Check Inventory</div>
          <div class="table-cell means-cell">Review what you have</div>
          <div class="table-cell matters-cell">Use items before they spoil</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Plan Meals</div>
          <div class="table-cell means-cell">Design balanced meals</div>
          <div class="table-cell matters-cell">Include all food groups</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Make List</div>
          <div class="table-cell means-cell">List needed ingredients</div>
          <div class="table-cell matters-cell">Organize by store section</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Activity: Weekly Planning</strong>',
      'Create a simple meal plan for the next three days:',
      '• List meals for breakfast, lunch, and dinner',
      '• Check your pantry for ingredients',
      '• Make a shopping list for missing items'
    ]
  },
  {
    title: 'Smart Shopping Strategies',
    content: [
      'Maximize your grocery budget with these strategies:',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header">Strategy</div>
          <div class="table-header">Method</div>
          <div class="table-header">Benefits</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Buy Seasonal</div>
          <div class="table-cell means-cell">Choose local, in-season produce</div>
          <div class="table-cell matters-cell">Better prices, fresher food</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Bulk Buying</div>
          <div class="table-cell means-cell">Purchase staples in larger quantities</div>
          <div class="table-cell matters-cell">Lower cost per unit</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Compare Prices</div>
          <div class="table-cell means-cell">Check unit prices</div>
          <div class="table-cell matters-cell">Best value for money</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Smart Shopping Tips:</strong>',
      '• Never shop hungry',
      '• Stick to your list',
      '• Check unit prices',
      '• Buy whole foods over processed'
    ]
  },
  {
    title: 'Food Preparation and Storage',
    content: [
      'Essential food prep and storage guidelines:',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header">Food Type</div>
          <div class="table-header">Prep Method</div>
          <div class="table-header">Storage Tips</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Vegetables</div>
          <div class="table-cell means-cell">Wash, cut, portion</div>
          <div class="table-cell matters-cell">Air-tight containers, use within 5 days</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Proteins</div>
          <div class="table-cell means-cell">Portion, marinate</div>
          <div class="table-cell matters-cell">Freeze unused portions</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Grains</div>
          <div class="table-cell means-cell">Cook in batches</div>
          <div class="table-cell matters-cell">Store cooked in fridge, dry in pantry</div>
        </div>
      </div>`
    ]
  },
  {
    title: 'Weekly Planning Challenge',
    content: [
      'Put your learning into practice:',
      '• <strong>Sunday:</strong> Plan your week\'s meals',
      '• <strong>Monday:</strong> Shop with your organized list',
      '• <strong>Tuesday-Friday:</strong> Follow your plan',
      '• <strong>Saturday:</strong> Evaluate and adjust for next week',
      '<br/>',
      '<strong>Track Your Progress:</strong>',
      '• Did you stay on plan?',
      '• How much did you save?',
      '• Did you waste less food?',
      '• What would you change?'
    ],
    illustrationText: 'Meal Planning'
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      "You've completed the module content! Now, let's see how well you understand meal planning and preparation strategies."
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your understanding of meal planning, budgeting, and food preparation techniques.',
        'The quiz will help you review key concepts about planning meals, shopping smart, and preparing food efficiently.'
      ]
    }
  }
];

const NutritionalModule5Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('nutritionalM5');
  const totalSteps = moduleContent.length;

  // Ensure currentStep is within bounds
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/nutritional-module-5/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/nutritional-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  // Error boundary for undefined module
  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div 
          className="module-left nutritional-panel"
        >
          <div className="module-header">
            <h1>Module 5</h1>
            <p className="module-subtitle">Mindful Eating & Self-Awareness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Develop a healthier relationship with food through mindful eating practices and greater awareness of your eating habits.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content"> 
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title"> 
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            
            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/nutritional-module-5/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule5Page;

